import React, { useLayoutEffect, useRef } from 'react';

// For a single-line filter row: the first child that does not fully fit inside
// the row, and every child after it, is taken out of the layout instead of
// showing half-cut at the screen edge (phones). The children that stay keep the
// freed space. Re-measured when the row resizes or children come and go (e.g.
// "Clear All Filters" appearing).
export function useHideOverflowing<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const row = ref.current;
    if (!row) return;

    const update = () => {
      const children = Array.from(row.children) as HTMLElement[];
      // Show everything first so each child is measured at its natural place;
      // all of this runs before the browser paints, so nothing flickers.
      children.forEach((child) => {
        if (child.dataset.overflowHidden) {
          child.style.display = '';
          delete child.dataset.overflowHidden;
        }
      });
      const right = row.getBoundingClientRect().right;
      const firstCut = children.findIndex(
        (child) => child.getBoundingClientRect().right > right + 0.5,
      );
      if (firstCut === -1) return;
      children.slice(firstCut).forEach((child) => {
        child.dataset.overflowHidden = 'true';
        child.style.display = 'none';
      });
    };

    update();
    // jsdom (tests) has no ResizeObserver; the one-off measurement above is enough there.
    if (typeof ResizeObserver === 'undefined') return;
    // Only the row is watched: its width comes from the page, not from the
    // children, so hiding them cannot re-trigger this observer.
    const resize = new ResizeObserver(update);
    resize.observe(row);
    const mutations = new MutationObserver(update);
    mutations.observe(row, { childList: true });
    return () => {
      resize.disconnect();
      mutations.disconnect();
    };
  }, []);

  return ref;
}

// For a group that must be all-or-nothing on one line (e.g. suggestion chips):
// the element is a shrinkable flex item that never wraps, and it is made
// invisible whenever its content is wider than the room it gets. Its width comes
// from the row, not from its content, so hiding it never changes the layout.
// `deps` re-attaches the observer when the element mounts conditionally.
export function useHideWhenClipped<T extends HTMLElement>(
  deps: React.DependencyList = [],
) {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      el.style.visibility =
        el.scrollWidth > el.clientWidth + 0.5 ? 'hidden' : '';
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const resize = new ResizeObserver(update);
    resize.observe(el);
    return () => resize.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
